import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { Skill } from '../types';

import { AddChartOverlayTool } from 'js/src/agent/tools/AddChartOverlayTool';
import { AddChartSceneTool } from 'js/src/agent/tools/AddChartSceneTool';
import { AddVoiceoverSceneTool } from 'js/src/agent/tools/AddVoiceoverSceneTool';
import { EditChartTool } from 'js/src/agent/tools/EditChartTool';

export const chartsBasicsSkill: Skill = {
    name: SKILL_NAMES.chartsBasics,
    description: 'Load BEFORE adding a chart to a video or editing an existing chart.',
    kind: 'howto',
    surfaces: ['sketch'],
    get instructions(): string {
        return `# Charts Basics

Stats worth seeing become charts, not plain text. This skill covers picking the
right chart tool, writing the \`data\` parameter well, and fixing a chart that came
out wrong.

## Which tool
- **\`${AddChartSceneTool.toolName}\`** — a NEW dedicated chart scene: full-scene chart on a clean
  background, front-and-center, so it never buries existing scene text. Use on
  text-only videos or wherever the chart deserves its own scene. It's added at the end
  of the video by default; pass \`after_scene_group_id\` to place it right after a
  specific scene group instead.
- **\`${AddChartOverlayTool.toolName}\`** — a chart cutaway inside a voiceover/talking-head scene
  group (identified by its \`scene_group_id\`), covering a chosen span of transcript
  words (picked like \`${AddVoiceoverSceneTool.toolName}\`).
- **\`${EditChartTool.toolName}\`** — fix an existing chart's data or change its type. Works on
  charts from chart scene designs and from the chart tools; find the chart asset
  (type CHART) in the video state for its \`scene_id\`/\`asset_name\`.

## Writing the data parameter
- Describe what the chart should show and include the actual numbers/labels inline
  when you have them: "quarterly revenue: Q1 1.2M, Q2 1.8M, Q3 1.5M, Q4 2.1M" or
  "coin-flip outcomes — Heads 6, Tails 4". The description tells the generator what
  the chart is and how to label it; embedded figures pin the exact numbers.
- No figures? Describe the data and the generator supplies them (it may estimate).
- Keep values in the form you want displayed — percentages stay percentages, counts
  stay counts; the chart does not convert between them.
- The chart takes exactly as many bars/lines/slices as the data needs — you're not
  limited to a fixed number of series.

## After adding
The chart may briefly show placeholder data before generation completes. Check a
rendered screenshot; if a label or value is wrong, or the type should change, fix
it with \`${EditChartTool.toolName}\`.

## The ${EditChartTool.toolName} contract
An edit fully re-specifies the chart: ALWAYS provide BOTH \`chart_type\` and the full
\`data\`, even when only one is changing — the data replaces the whole dataset.
- Fixing a value: pass the chart's CURRENT type plus the corrected full dataset
  ("the chart says Q5, should be Q4" → chart_type "bar", data "Q1: 120, Q2: 200,
  Q3: 150, Q4: 210").
- Switching type: pass the new type plus the chart's current values read from the
  video state ("make it a line chart" → chart_type "line", data with the existing
  points restated).`;
    },
};
